import React, { useEffect, useState } from "react";
import { Layers, Search, RotateCcw, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

// ── Interfaces ────────────────────────────────────────────────────────────────
interface VlanData {
    id: number;
    vlan_id: number;
    name: string;
    status: string | null;
    source: string | null;
    vlan_type: string | null;
    vlan_status: string | null;
    egress_ports: string[] | string | null;
    untagged_ports: string[] | string | null;
    tagged_ports: string[] | string | null;
}

interface VlansInfoProps {
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
    onDataLoaded?: (hasData: boolean) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────


const parsePortList = (ports: string[] | string | null): string[] => {
    if (!ports) return [];
    if (Array.isArray(ports)) return ports;
    try {
        const parsed = JSON.parse(ports);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

// ── Badges & Port List ────────────────────────────────────────────────────────
// ── Badges & Port List ────────────────────────────────────────────────────────
const StatusBadge = ({ value }: { value: string | null }) => {
    if (!value) return <span className="text-slate-400 dark:text-gray-200 text-[9px]">—</span>;
    const lower = value.toLowerCase();
    const style =
        lower === "active" || lower === "up"
            ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
            : lower === "inactive" || lower === "down"
                ? "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
                : "bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-700 dark:text-gray-200";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${style}`}>
            {value}
        </span>
    );
};

const TypeBadge = ({ value }: { value: string | null }) => {
    if (!value) return <span className="text-slate-400 dark:text-gray-200 text-[9px]">—</span>;
    const lower = value.toLowerCase();
    const style =
        lower === "static"
            ? "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
            : lower === "dynamic"
                ? "bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
                : "bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-700 dark:text-gray-200";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${style}`}>
            {value}
        </span>
    );
};

const PortList = ({ ports, color }: { ports: string[] | string | null; color: string }) => {
    const list = parsePortList(ports);
    if (list.length === 0)
        return <span className="text-slate-400 dark:text-gray-200 text-[9px]">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {list.map((p, i) => (
                <span key={i} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-mono border ${color}`}>
                    {p}
                </span>
            ))}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
const VlansInfo: React.FC<VlansInfoProps> = ({
    hideHeader = false,
    defaultItemsPerPage = 12,
    onDataLoaded,
}) => {
    const [data, setData] = useState<VlanData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const queryObj: any = { asset_id, client_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { source: { "$ilike": `%${debouncedSearchTerm}%` } },
                ];
            }
            const params: any = { query: JSON.stringify(queryObj), skip, limit: itemsPerPage };
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }
            const response = await baseapi.get("/asset_vlans", { params });
            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
                onDataLoaded?.((response.data.data || []).length > 0);
            } else {
                showError(response.data.message);
                onDataLoaded?.(false);
            }
        } catch (error: any) {
            showError(error.message);
            onDataLoaded?.(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => { fetchData(); }, [currentPage, itemsPerPage, debouncedSearchTerm, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={10} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className="animate-fade-in">

            {/* ── Header ─────────────────────────────────────────────────── */}
            {!hideHeader && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Layers className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">VLANs</h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search VLANs..."
                                className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-4 py-2 text-[11px] text-gray-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 w-52"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={fetchData}
                            className="p-2 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <RotateCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>
            )}

            {/* ── Table ──────────────────────────────────────────────────── */}
            <div className="rounded-t-xl overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-16">
                        <RotateCcw size={16} className="animate-spin text-orange-400" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">Loading VLANs...</span>
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 opacity-40">
                        <Layers size={22} className="text-slate-400 dark:text-gray-200" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No VLANs found</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto bg-white dark:bg-gray-900/60 border border-slate-200 dark:border-slate-700 rounded-t-xl shadow-sm">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('vlan_id')}>
                                        <div className="flex items-center gap-1">
                                            VLAN ID
                                            <SortIcon column="vlan_id" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('name')}>
                                        <div className="flex items-center gap-1">
                                            Name
                                            <SortIcon column="name" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('vlan_type')}>
                                        <div className="flex items-center gap-1">
                                            Type
                                            <SortIcon column="vlan_type" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('vlan_status')}>
                                        <div className="flex items-center gap-1">
                                            Status
                                            <SortIcon column="vlan_status" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('egress_ports')}>
                                        <div className="flex items-center gap-1">
                                            Egress Ports
                                            <SortIcon column="egress_ports" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('tagged_ports')}>
                                        <div className="flex items-center gap-1">
                                            Tagged Ports
                                            <SortIcon column="tagged_ports" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('untagged_ports')}>
                                        <div className="flex items-center gap-1">
                                            Untagged Ports
                                            <SortIcon column="untagged_ports" />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((vlan, _i) => (
                                    <tr
                                        key={vlan.id}
                                        className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors align-top"
                                    >


                                        {/* VLAN ID */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <span className="text-orange-500 font-black text-[11px]">#</span>
                                                <span className="text-[13px] font-black text-gray-900 dark:text-white">{vlan.vlan_id}</span>
                                            </div>
                                        </td>

                                        {/* Name */}
                                        <td className="px-4 py-3">
                                            <span className="text-[11px] font-bold text-slate-700 dark:text-gray-200">
                                                {!vlan.name || vlan.name === '""' ? "—" : vlan.name}
                                            </span>
                                        </td>

                                        {/* Type */}
                                        <td className="px-4 py-3">
                                            <TypeBadge value={vlan.vlan_type} />
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3">
                                            <StatusBadge value={vlan.vlan_status ?? vlan.status} />
                                        </td>

                                        {/* Egress Ports */}
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <PortList
                                                ports={vlan.egress_ports}
                                                color="bg-slate-100 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600/40 text-slate-700 dark:text-gray-200"
                                            />
                                        </td>

                                        {/* Tagged Ports */}
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <PortList
                                                ports={vlan.tagged_ports}
                                                color="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300"
                                            />
                                        </td>

                                        {/* Untagged Ports */}
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <PortList
                                                ports={vlan.untagged_ports}
                                                color="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-300"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Pagination ─────────────────────────────────────────────── */}
            {!loading && totalItems > 0 && (
                <div className="px-2 rounded-b-xl py-1.5 flex justify-between items-center bg-slate-50 dark:bg-gray-900/60 border border-t-0 border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={val => { setItemsPerPage(val); setCurrentPage(1); }}
                        />
                        <span className="text-[10px] text-slate-500 dark:text-gray-500 font-medium whitespace-nowrap">
                            {totalItems} VLANs
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-1 text-slate-400 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1 text-slate-400 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VlansInfo;
